import { useCallback, useEffect, useState } from 'react';
import type { IdeaListItem, Paginated, StatsOverview } from '@shared/types';
import { api, errorMessage } from '../../lib/api';

/**
 * 首页数据。
 *
 * 四个请求并行发出，都是服务端已经算好的东西：
 * 统计数据、我参与的（找未读）、我认领的（在推进）、我提的（草稿）。
 * 不额外做「最近活跃创意」接口 —— 最近动态直接用统计里的 activity 流。
 */

const NO_STATS: StatsOverview = {
  total: 0,
  byStatus: [],
  byTag: [],
  ai: {
    runs: 0,
    succeeded: 0,
    failed: 0,
    goCount: 0,
    needsMoreInfoCount: 0,
    noGoCount: 0,
    avgScore: null,
    passRate: null,
  },
  people: { authors: 0, assignees: 0 },
  recentEvents: [],
  myUsage: {
    requests: 0,
    meteredRequests: 0,
    promptTokens: 0,
    completionTokens: 0,
    reasoningTokens: 0,
    totalTokens: 0,
    succeeded: 0,
    failed: 0,
  },
};

export interface HomeHighlights {
  /** 我参与过、且有新动态（别人评论或改过状态）的创意 */
  unread: IdeaListItem[];
  /** 我认领的、仍在推进中（未上线、未归档）的创意数量 */
  assignedInProgress: number;
  /** 还是草稿、没提交的创意数量 */
  drafts: number;
  /** 已提交但还没跑过研判的创意数量 —— 这是「现在最该去做的一件事」 */
  awaitingValidation: number;
}

/**
 * 从三个列表里算出首页要强调的几个数。
 *
 * 抽成纯函数是因为「什么算需要我关注」是产品判断而不是渲染细节：
 * 它必须能被单测固定住，否则改一处筛选条件就会悄悄改变首页的引导方向。
 */
export function deriveHomeHighlights(input: {
  involved: readonly IdeaListItem[];
  assigned: readonly IdeaListItem[];
  authored: readonly IdeaListItem[];
}): HomeHighlights {
  const unread = input.involved.filter((idea) => idea.viewer.unread);

  const assignedInProgress = input.assigned.filter(
    (idea) => idea.status !== 'shipped' && idea.status !== 'archived',
  ).length;

  const drafts = input.authored.filter((idea) => idea.status === 'draft').length;

  // 我提的与我认领的可能重叠（自己认领自己的创意），先去重再数
  const pendingValidation = [...input.authored, ...input.assigned].filter(
    (idea, index, all) => idea.status === 'submitted' && all.findIndex((candidate) => candidate.id === idea.id) === index,
  ).length;

  return { unread, assignedInProgress, drafts, awaitingValidation: pendingValidation };
}

/** 按本地时间给一句问候。深夜与凌晨同一句，不假装热情。 */
export function greetingFor(hour: number): string {
  if (hour >= 5 && hour < 11) return '早上好';
  if (hour >= 11 && hour < 13) return '中午好';
  if (hour >= 13 && hour < 18) return '下午好';
  if (hour >= 18 && hour < 23) return '晚上好';
  return '夜深了';
}

export interface HomeData {
  stats: StatsOverview;
  highlights: HomeHighlights;
  loading: boolean;
  error: string | null;
}

const EMPTY_HIGHLIGHTS: HomeHighlights = { unread: [], assignedInProgress: 0, drafts: 0, awaitingValidation: 0 };

export function useHomeData(): HomeData {
  const [stats, setStats] = useState<StatsOverview>(NO_STATS);
  const [highlights, setHighlights] = useState<HomeHighlights>(EMPTY_HIGHLIGHTS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (signal: AbortSignal) => {
    try {
      const [statsResult, involved, assigned, authored] = await Promise.all([
        api.get<StatsOverview>('/api/ideas/stats', { signal }),
        api.get<Paginated<IdeaListItem>>('/api/ideas/mine', { query: { scope: 'involved', pageSize: 200 }, signal }),
        api.get<Paginated<IdeaListItem>>('/api/ideas/mine', {
          query: { scope: 'assigned', includeArchived: 1, pageSize: 200 },
          signal,
        }),
        api.get<Paginated<IdeaListItem>>('/api/ideas/mine', {
          query: { scope: 'authored', includeArchived: 1, pageSize: 200 },
          signal,
        }),
      ]);
      if (signal.aborted) return;

      setStats(statsResult);
      setHighlights(
        deriveHomeHighlights({ involved: involved.items, assigned: assigned.items, authored: authored.items }),
      );
      setError(null);
    } catch (fetchError) {
      if (signal.aborted) return;
      setError(errorMessage(fetchError));
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  return { stats, highlights, loading, error };
}
